import { useEffect, useMemo } from "react";
import { useUserStore } from "@/store/user";
import { BalanceType } from "@/types/user";
import { getEffectivePaidFloor, getPaidCreditsRequirementMessage } from "../creditsPaymentAmount";

type UsePurchasedCreditsGateOptions = {
  /**
   * Minimum paid (non-promotional) credit balance required to pay with credits.
   * When 0 / undefined there is no minimum and the gate is a no-op.
   * Backend derives this absolute USDC amount from its configured paid/promo ratio.
   */
  minNonPromotionalCreditsRequired?: number;
  /**
   * Total booking cost used to derive the paid-share percentage in the requirement message.
   * Optional — falls back to 70% in copy when missing.
   */
  totalCost?: number;
  /**
   * When false, the gate is inert (no message, no CTA block) and balances are not fetched.
   * Call sites should only enable when the payment UI is active *and* the selected
   * payment method is credits — other methods never surface this requirement.
   */
  enabled?: boolean;
};

export type PurchasedCreditsGate = {
  /** True when the gate is active (credits pay, min > 0, user holds promo) and a min is set. */
  hasRequirement: boolean;
  /** True once the balance needed to evaluate the gate has been loaded. */
  isReady: boolean;
  /** The user's paid (withdrawable, non-promotional) credit balance. */
  purchasedCreditsBalance: number;
  /** Approximate promotional (non-withdrawable) balance; 0 when none. */
  promotionalCreditsBalance: number;
  /** True when the informational shortfall banner applies: promo present, paid < min.
   * Does not disable the CTA — pay flow tops up the paid/cost shortfall instead. */
  isBlocked: boolean;
  /** User-facing requirement message when the gate is active; empty when inert. */
  message: string;
};

const inertGate = (
  isReady: boolean,
  purchasedCreditsBalance: number,
  promotionalCreditsBalance = 0
): PurchasedCreditsGate => ({
  hasRequirement: false,
  isReady,
  purchasedCreditsBalance,
  promotionalCreditsBalance,
  isBlocked: false,
  message: ""
});

/**
 * Surfaces the backend `min_non_promotional_credits_required` requirement for **credits payment only**,
 * when the user holds promotional credits: warn if paid (withdrawable) balance is below the floor.
 * The CTA stays enabled — pay flow tops up max(cost shortfall, paid shortfall).
 *
 * Promo presence is inferred as:
 *   max(0, credits_balance - credits_withdrawable_balance)
 * (balances API has no dedicated promo field).
 *
 * When `enabled` is false, min is 0, balances are unknown, or there is no promo balance,
 * the gate is inert (no message / CTA block from this rule). Users with no credits are unaffected.
 */
export const usePurchasedCreditsGate = ({
  minNonPromotionalCreditsRequired,
  totalCost,
  enabled = true
}: UsePurchasedCreditsGateOptions): PurchasedCreditsGate => {
  const balances = useUserStore((state) => state.balances);
  const fetchBalances = useUserStore((state) => state.fetchBalances);

  const required = Number(minNonPromotionalCreditsRequired || 0);
  const isGateActive = enabled && required > 0;

  useEffect(() => {
    if (isGateActive) {
      void fetchBalances();
    }
  }, [isGateActive, fetchBalances]);

  return useMemo(() => {
    const isReady = Boolean(balances);
    const purchasedCreditsBalance = Number(
      (balances && balances[BalanceType.CREDITS_WITHDRAWABLE]) || 0
    );
    const totalSpendableCredits = Number((balances && balances[BalanceType.CREDITS]) || 0);
    const courtesyCreditsBalance = Number(
      (balances && balances[BalanceType.CREDITS_COURTESY]) || 0
    );
    // No dedicated promo field: spendable that is neither paid (withdrawable) nor
    // courtesy is treated as promotional.
    const promotionalCreditsBalance = Math.max(
      0,
      totalSpendableCredits - purchasedCreditsBalance - Math.max(0, courtesyCreditsBalance)
    );
    const hasPromotionalCredits = promotionalCreditsBalance > 0;

    // Courtesy is exempt from the paid floor: the floor scales to the courtesy-uncovered
    // remainder (mirrors the backend). Needs the booking cost — when unknown the gate
    // can't quantify the floor and stays inert below.
    const effectiveRequired = getEffectivePaidFloor(
      required,
      Number(totalCost) || 0,
      Math.max(0, courtesyCreditsBalance)
    );

    if (!isGateActive) {
      return inertGate(isReady, purchasedCreditsBalance, promotionalCreditsBalance);
    }

    // Gate is only explanatory for promo-holding accounts whose paid floor survives
    // courtesy. Pure paid / empty / fully-courtesy-covered accounts skip this UX.
    if (!isReady || !hasPromotionalCredits || effectiveRequired <= 0) {
      return inertGate(isReady, purchasedCreditsBalance, promotionalCreditsBalance);
    }

    const isBlocked = purchasedCreditsBalance < effectiveRequired;
    const requirementMessage = getPaidCreditsRequirementMessage({
      required: effectiveRequired,
      totalCost
    });
    const message = isBlocked
      ? `${requirementMessage} Add paid credits to continue.`
      : requirementMessage;

    return {
      hasRequirement: true,
      isReady,
      purchasedCreditsBalance,
      promotionalCreditsBalance,
      isBlocked,
      message
    };
  }, [balances, isGateActive, required, totalCost]);
};
